import React, { useLayoutEffect, useRef } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BlockAction } from '../../Blocks/BlockAction';
import * as styles from './BlocksContainer.module.scss';
import { TemplateBlock, BlockID } from 'concepts/Extraction/Blocks/BaseBlock';
import { OrderedList } from 'system/base/List';
import { Factory } from 'concepts/Extraction/Blocks/factory';
import { SparkleWithTooltip } from 'concepts/AutoDocExtraction/Sparkle/Sparkle';
import { SUGGESTIBLE_BLOCK_TYPES } from 'concepts/AutoDocExtraction/suggestionFieldMatcher';

export type BlocksContainerProps<Block extends TemplateBlock> = {
  blocks: Block[];
  dispatch: (action: BlockAction<Block>) => void;
  renderMenu: (block: Block) => JSX.Element;
  selectedBlockID: BlockID | null;
  isFieldSuggestable?: (label: string) => boolean;
};

type BlockRowProps<Block extends TemplateBlock> = {
  block: Block;
  index: number;
  isSelected: boolean;
  dispatch: (action: BlockAction<Block>) => void;
  renderMenu: (block: Block) => JSX.Element;
  isFieldSuggestable: (label: string) => boolean;
};

function BlockRowComponent<Block extends TemplateBlock>({
  block,
  index,
  isSelected,
  dispatch,
  renderMenu,
  isFieldSuggestable,
}: BlockRowProps<Block>): JSX.Element {
  const { Editor, TypeIcon } = Factory(block);

  return (
    <Draggable index={index} draggableId={block.id}>
      {(provided) => (
        <li
          data-block-id={block.id}
          className={isSelected ? styles.selected : styles.container}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={styles.actions}>
            {renderMenu(block)}
            <div role="button" {...provided.dragHandleProps}>
              {TypeIcon ? <TypeIcon /> : <></>}
            </div>
          </div>
          <div className={styles.editorCell}>
            <Editor
              block={block}
              index={index}
              onChange={(updatedBlock) => {
                dispatch({
                  type: 'update',
                  block: updatedBlock as Block,
                });
              }}
              onRemove={() => {
                dispatch({ type: 'remove', blockID: block.id });
              }}
              onComplete={() => {
                dispatch({ type: 'complete', blockID: block.id });
              }}
            />
            {/*
              Places a sparkle right after the visible end of
              the block's label. The label is rendered by an
              <input>, and an input can't have an icon sitting
              inside its text — so we use a "mirror" trick: an
              invisible copy of the label text is laid on top of
              the input, in the exact same font and position.
              That copy takes up exactly as much space as the
              visible text, so the sparkle rendered right after
              it lands just past the last character — and stays
              there as the user types, because the copy
              re-renders with every keystroke. The overlay
              ignores mouse clicks (so the input underneath
              still works normally) and is hidden from screen
              readers so the sparkle doesn't get read out loud
            */}
            {SUGGESTIBLE_BLOCK_TYPES.includes(block.type) &&
              isFieldSuggestable(block.label) && (
                <div className={styles.sparkleOverlay}>
                  <span
                    className={styles.sparkleOverlayLabel}
                    aria-hidden="true"
                  >
                    {block.label}
                  </span>
                  <span className={styles.sparkleOverlayIcon}>
                    <SparkleWithTooltip iconStyle="solid" label={block.label} />
                  </span>
                </div>
              )}
          </div>
        </li>
      )}
    </Draggable>
  );
}

const BlockRow = React.memo(BlockRowComponent) as typeof BlockRowComponent;

const noFieldsSuggestable = () => false;

export function BlocksContainer<Block extends TemplateBlock>({
  blocks,
  renderMenu,
  dispatch,
  selectedBlockID,
  isFieldSuggestable = noFieldsSuggestable,
}: BlocksContainerProps<Block>): JSX.Element {
  const elRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = elRef.current;
    if (el == null) {
      return;
    }

    const listener = (event: FocusEvent) => {
      // Ignore <button> focus from @reach/menu-button that it schedules
      // using requestAnimationFrame() that break our tests.
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        const li = event.target.closest('[data-block-id]');
        if (li instanceof HTMLElement) {
          const blockID = li.dataset.blockId;
          if (blockID) {
            dispatch({ type: 'selectBlock', blockID });
          }
        }
      }
    };
    el.addEventListener('focus', listener, true);

    return () => {
      el.removeEventListener('focus', listener, true);
    };
  }, [dispatch]);

  return (
    <DragDropContext
      onDragEnd={({ destination, source }) => {
        if (destination) {
          const block = blocks[source.index];
          if (block) {
            dispatch({
              type: 'move',
              blockID: block.id,
              to: destination.index,
            });
          }
        }
      }}
    >
      <Droppable droppableId="block-droppable-1">
        {(provided) => (
          <div ref={elRef}>
            <OrderedList
              variant="unbulleted"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {blocks.map((block, index) => (
                <BlockRow
                  key={block.id}
                  block={block}
                  index={index}
                  isSelected={selectedBlockID === block.id}
                  dispatch={dispatch}
                  renderMenu={renderMenu}
                  isFieldSuggestable={isFieldSuggestable}
                />
              ))}
              {provided.placeholder}
            </OrderedList>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
